import type { VariantStatsView } from '../../../api/client.ts';
import { formatInterval, formatP, formatPercent, formatPoints, formatUplift, groupName } from './format.ts';

/**
 * One row per variant and control (BB-136): who was counted (assigned and received), conversions, the rate with its
 * 95 % interval, the difference and uplift against control, p before and after Holm correction, Braze-style confidence,
 * whether the group reached the minimum sample, and aborted sends. Control has no comparison of its own.
 */
export function VariantStatsTable({ data }: { data: VariantStatsView }) {
  const denominator = (r: VariantStatsView['rows'][number]) => (data.basis === 'assigned' ? r.assigned : r.exposed);
  return (
    <div className="push-table-scroll">
      <table className="table push-table variant-stats-table" aria-label="Variants against control">
        <thead>
          <tr>
            <th scope="col">Group</th>
            <th scope="col">Assigned</th>
            <th scope="col">Received</th>
            <th scope="col">Converted</th>
            <th scope="col">Rate</th>
            <th scope="col">95 % interval</th>
            <th scope="col">Difference</th>
            <th scope="col">Uplift</th>
            <th scope="col">p-value</th>
            <th scope="col">p (Holm)</th>
            <th scope="col">Confidence</th>
            <th scope="col">Sample</th>
            <th scope="col">Aborted</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => {
            const winner = data.winner === r.variantApiId;
            return (
              <tr key={r.variantApiId} className={winner ? 'variant-stats-table__winner' : undefined}>
                <th scope="row">
                  {groupName(r)}
                  {winner && <span className="variant-stats-table__tag variant-stats-table__tag--winner">Winner</span>}
                  {!r.isControl && r.significant && !winner && <span className="variant-stats-table__tag">Significant</span>}
                  {r.skewedByAborts && <span className="push-table__rate">Kept users a variant would have dropped</span>}
                </th>
                <td>{r.assigned.toLocaleString('en-US')}</td>
                <td>{r.exposed.toLocaleString('en-US')}</td>
                <td>{r.conversions.toLocaleString('en-US')}</td>
                <td>{r.rate === null ? '—' : formatPercent(r.rate)}</td>
                <td>{formatInterval(r.ci95)}</td>
                <td>{r.isControl ? 'baseline' : formatPoints(r.differenceVsControl)}</td>
                <td>{r.isControl ? '—' : formatUplift(r.upliftVsControl)}</td>
                <td>{formatP(r.pValue)}</td>
                <td>{formatP(r.pValueAdjusted)}</td>
                <td>{r.confidence === null ? '—' : formatPercent(r.confidence)}</td>
                <td>
                  <span className="push-table__count">{r.sampleStatus === 'sufficient' ? 'Enough' : 'Not enough'}</span>
                  {data.requiredPerVariant !== null && (
                    <span className="push-table__rate">
                      {denominator(r).toLocaleString('en-US')} of {data.requiredPerVariant.toLocaleString('en-US')}
                    </span>
                  )}
                </td>
                <td>{r.aborted === null ? '—' : r.aborted.toLocaleString('en-US')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
